import { ArrowRight, ArrowUp, Sparkles } from 'lucide-react';

/**
 * Lightweight mock of the Rose widget for the onboarding side panel (IX-3922).
 *
 * This is intentionally NOT the real embedded widget (that needs a provisioned
 * site + config, which does not exist yet during onboarding). It mirrors the
 * design prototype's `RoseWidget` / `RoseAnswerCard`: a faux chat card. The
 * `widget` variant reflects the answers the user is giving in the wizard —
 * company name, tone-driven greeting, and suggested questions — so they watch
 * their agent take shape as they type. The `answer` variant is the static
 * value pitch shown on the sign-in screen.
 */

// Keyed by behavior-schema preset label (see behavior.schema.json `ui:presets`),
// which is what the onboarding wizard passes as `tone`.
const TONE_GREETING: Record<string, string> = {
    Professional: "Hi! I'm the {co} assistant. What would you like to know?",
    'Sales-oriented': 'Hey there! Excited to show you what {co} can do — where should we start?',
};

const STARTER_QUESTIONS = [
    'What does {co} do?',
    'How much does it cost?',
    'Do you integrate with my CRM?',
];

function companyFromUrl(url: string): string {
    const host = url
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .split('/')[0];
    const base = host.split('.')[0];
    return base ? base.charAt(0).toUpperCase() + base.slice(1) : '';
}

function greetingFor(tone: string, company: string): string {
    return (TONE_GREETING[tone] ?? TONE_GREETING.Professional).replace('{co}', company);
}

/** Rose pinwheel mark — the widget avatar glyph. */
function RoseMark({ className }: { className?: string }) {
    return (
        <svg viewBox="0 0 14 13" fill="none" className={className} aria-hidden="true">
            <g transform="translate(0, 0.4)" fill="currentColor">
                <path d="M6.61343 0V4.5C6.61343 5.16274 6.07617 5.7 5.41343 5.7H3.01343L6.61343 0Z" />
                <path d="M7.21323 12L7.21323 7.5C7.21323 6.83726 7.75049 6.3 8.41323 6.3L10.8132 6.3L7.21323 12Z" />
                <path d="M12.9133 5.7001L8.41333 5.7001C7.75059 5.7001 7.21333 5.16284 7.21333 4.5001L7.21333 2.1001L12.9133 5.7001Z" />
                <path d="M0.913331 6.2999L5.41333 6.2999C6.07607 6.2999 6.61333 6.83716 6.61333 7.4999L6.61333 9.8999L0.913331 6.2999Z" />
            </g>
        </svg>
    );
}

function PreviewHeader({
    name,
    subtitle,
    accent,
}: {
    name: string;
    subtitle: string;
    accent?: string;
}) {
    return (
        <div className="flex items-center gap-2.5 border-b border-border px-4 py-3">
            <span
                className={
                    accent
                        ? 'flex size-9 flex-none items-center justify-center rounded-full text-white'
                        : 'flex size-9 flex-none items-center justify-center rounded-full bg-surface-sunken text-foreground'
                }
                style={accent ? { background: accent } : undefined}
            >
                <RoseMark className="size-4" />
            </span>
            <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-foreground">{name}</div>
                <div className="truncate text-xs text-muted-foreground">{subtitle}</div>
            </div>
        </div>
    );
}

function PreviewComposer({ placeholder, accent }: { placeholder: string; accent?: string }) {
    return (
        <div className="flex items-center gap-2 rounded-full border border-border bg-card px-3.5 py-2.5">
            <Sparkles
                className="size-4 flex-none text-accent-foreground"
                style={accent ? { color: accent } : undefined}
            />
            <span className="flex-1 truncate text-sm text-muted-foreground">{placeholder}</span>
            <ArrowUp className="size-4 flex-none text-muted-foreground" />
        </div>
    );
}

interface RosePreviewProps {
    company?: string;
    tone?: string;
    url?: string;
    /**
     * Client's brand colour (hex). Applied to the accented chrome so the preview
     * matches the colour they picked; omitted falls back to the accent token.
     * Ignored by the `answer` variant — that card is Rose's own pitch.
     */
    brandColor?: string;
    /** `widget` = live wizard mirror; `answer` = static Q&A card for sign-in. */
    variant?: 'widget' | 'answer';
}

export function RosePreview({
    company,
    tone,
    url = '',
    brandColor,
    variant = 'widget',
}: RosePreviewProps) {
    const name = company?.trim() || companyFromUrl(url) || 'Your company';
    const accent = brandColor?.trim() || undefined;

    if (variant === 'answer') {
        return (
            <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-lg">
                <PreviewHeader name="Rose" subtitle="Sales agent · online" />
                <div className="space-y-3 px-4 py-4">
                    <div className="ml-auto w-fit max-w-[85%] rounded-2xl rounded-br-md bg-accent px-3 py-2 text-sm text-foreground">
                        Why should I use Rose on my website?
                    </div>
                    <div className="space-y-3 text-sm leading-relaxed text-foreground">
                        <p>
                            Rose is a 24/7 sales rep for your site. It answers visitors instantly,
                            captures their email, and books demos on its own.
                        </p>
                        <p>
                            The result: more visitors engage and more of them convert — no rebuild
                            needed.
                        </p>
                        <p className="font-semibold">
                            Ready to see it on your own site? Sign up — it's free.
                        </p>
                    </div>
                </div>
                <div className="border-t border-border px-4 py-3">
                    <PreviewComposer placeholder="Reply to Rose…" />
                </div>
            </div>
        );
    }

    return (
        <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-lg">
            <PreviewHeader name={name} subtitle="AI sales agent · online" accent={accent} />
            <div className="space-y-3 px-4 py-4">
                <div className="w-fit max-w-[90%] rounded-2xl rounded-tl-md bg-surface-sunken px-3.5 py-2.5 text-sm text-foreground">
                    {greetingFor(tone ?? 'Professional', name)}
                </div>
                <div className="text-xs font-medium text-muted-foreground">Suggested questions</div>
                <div className="space-y-2">
                    {STARTER_QUESTIONS.map((q) => (
                        <div
                            key={q}
                            className="flex items-center justify-between gap-2 rounded-xl border border-border px-3 py-2.5 text-sm text-foreground"
                        >
                            <span className="truncate">{q.replace('{co}', name)}</span>
                            <ArrowRight
                                className="size-4 flex-none text-muted-foreground"
                                style={accent ? { color: accent } : undefined}
                            />
                        </div>
                    ))}
                </div>
            </div>
            <div className="border-t border-border px-4 py-3">
                <PreviewComposer placeholder={`Ask ${name} anything…`} accent={accent} />
            </div>
        </div>
    );
}
